import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/Button';
import FormField, { Input, Select } from '../components/FormField';

function fmt(d) {
  if (!d) return '–';
  return new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const STATUS_CFG = {
  open:      { label: 'Offen',       cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  closed:    { label: 'Geschlossen', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
  cancelled: { label: 'Abgesagt',    cls: 'bg-red-50 text-red-600 border-red-200' },
};

export default function FoodRunDetail() {
  const { id } = useParams();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [itemText, setItemText] = useState('');
  const [itemPrice, setItemPrice] = useState('');
  const [itemBusy, setItemBusy] = useState(false);
  const [itemError, setItemError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    api.get(`/api/food-runs/${id}`)
      .then((r) => {
        setRun(r.data);
        const mine = r.data.items.find((it) => it.user_id === user?.id);
        setItemText(mine ? mine.item_text : '');
        setItemPrice(mine && mine.price != null ? mine.price : '');
      })
      .catch(() => setRun(null))
      .finally(() => setLoading(false));
  }, [id, user]);

  useEffect(() => { load(); }, [load]);

  const canManage = run && user && (isAdmin || run.creator_id === user.id);
  const myItem = run && user ? run.items.find((it) => it.user_id === user.id) : null;

  const submitItem = async (e) => {
    e.preventDefault();
    setItemBusy(true); setItemError('');
    try {
      await api.post(`/api/food-runs/${id}/items`, { item_text: itemText, price: itemPrice || null });
      load();
    } catch (err) { setItemError(err.response?.data?.detail || 'Fehler beim Speichern'); }
    finally { setItemBusy(false); }
  };

  const removeItem = async () => {
    if (!myItem) return;
    setItemBusy(true);
    try { await api.delete(`/api/food-runs/${id}/items/${myItem.id}`); setItemText(''); setItemPrice(''); load(); }
    catch (err) { setItemError(err.response?.data?.detail || 'Fehler'); }
    finally { setItemBusy(false); }
  };

  const togglePaid = async (item) => {
    try { await api.put(`/api/food-runs/${id}/items/${item.id}/paid`, { paid: !item.paid }); load(); }
    catch { /* ignore */ }
  };

  const changeStatus = async (status) => {
    try { await api.put(`/api/food-runs/${id}`, { status }); load(); }
    catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
  };

  const deleteRun = async () => {
    if (!window.confirm('Food Run wirklich löschen?')) return;
    try { await api.delete(`/api/food-runs/${id}`); navigate('/food-runs'); }
    catch (err) { setError(err.response?.data?.detail || 'Löschen fehlgeschlagen'); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true); setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      await api.post(`/api/food-runs/${id}/photos`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      load();
    } catch (err) { setError(err.response?.data?.detail || 'Upload fehlgeschlagen'); }
    finally { setUploading(false); }
  };

  const deletePhoto = async (photoId) => {
    try { await api.delete(`/api/food-runs/${id}/photos/${photoId}`); load(); }
    catch { /* ignore */ }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-2 border-ocean-500 border-t-transparent" /></div>;
  if (!run) return (
    <div className="max-w-2xl mx-auto card text-center py-14">
      <p className="text-slate-400 text-sm mb-3">Food Run nicht gefunden.</p>
      <Link to="/food-runs" className="text-sm font-semibold text-ocean-600 hover:text-ocean-800 no-underline">← Zur Übersicht</Link>
    </div>
  );

  const cfg = STATUS_CFG[run.status] || STATUS_CFG.open;

  return (
    <div className="max-w-2xl mx-auto">
      <Link to="/food-runs" className="text-sm text-ocean-600 hover:underline mb-4 inline-block no-underline">← Alle Food Runs</Link>

      <div className="flex flex-wrap gap-4 justify-between items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold text-ocean-900 tracking-tight">🍔 {run.restaurant_name}</h1>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${cfg.cls}`}>{cfg.label}</span>
            {run.restaurant_url && <a href={run.restaurant_url} target="_blank" rel="noreferrer" className="text-sm text-ocean-600 hover:underline">Website</a>}
            {run.menu_url && <a href={run.menu_url} target="_blank" rel="noreferrer" className="text-sm text-ocean-600 hover:underline">Speisekarte</a>}
          </div>
        </div>
      </div>

      {error && <div className="alert-error mb-4">{error}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="card">
          <p className="section-title">Details</p>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Abfahrt</dt><dd className="font-medium">{fmt(run.departure_time)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Bestellschluss</dt><dd className="font-medium">{fmt(run.order_deadline)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Ort</dt><dd className="font-medium">{run.location || '–'}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Organisiert von</dt><dd className="font-medium">{run.creator?.nickname || run.creator?.username || '–'}</dd></div>
          </dl>
        </div>
        <div className="card">
          <p className="section-title">Zahlung</p>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Barzahlung</dt><dd className="font-medium">{run.accepts_cash ? 'Möglich' : 'Nicht möglich'}</dd></div>
          </dl>
          {run.paypal_info && <p className="text-sm text-slate-700 whitespace-pre-wrap mt-2">{run.paypal_info}</p>}
        </div>
      </div>

      {/* Menu photos */}
      {(run.photos.length > 0 || canManage) && (
        <div className="card mb-6">
          <p className="section-title">Speisekarte (Fotos)</p>
          {run.photos.length === 0 ? (
            <p className="text-sm text-slate-400">Noch keine Fotos hochgeladen.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
              {run.photos.map((p) => (
                <div key={p.id} className="relative group">
                  <a href={p.photo_url} target="_blank" rel="noreferrer">
                    <img src={p.photo_url} alt="Speisekarte" className="w-full h-32 object-cover rounded-lg border border-slate-200" />
                  </a>
                  {canManage && (
                    <button onClick={() => deletePhoto(p.id)}
                      className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center border-0 cursor-pointer">
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          {canManage && (
            <label className="btn btn-secondary btn-sm inline-block cursor-pointer">
              {uploading ? 'Lädt hoch…' : '+ Foto hochladen'}
              <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} disabled={uploading} />
            </label>
          )}
        </div>
      )}

      {/* Order items */}
      <div className="card mb-6">
        <p className="section-title mb-4">Bestellungen ({run.items.length})</p>
        {run.items.length === 0 ? (
          <p className="text-sm text-slate-400 mb-4">Noch niemand hat bestellt.</p>
        ) : (
          <div className="space-y-2 mb-4">
            {run.items.map((it) => (
              <div key={it.id} className="flex items-center justify-between gap-3 border-b border-slate-50 last:border-0 py-2 text-sm">
                <div className="min-w-0">
                  <span className="font-semibold text-slate-800">{it.user?.nickname || it.user?.username || '–'}</span>
                  <span className="text-slate-500"> · {it.item_text}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {it.price != null && <span className="font-medium text-sand-700">{parseFloat(it.price).toFixed(2)} €</span>}
                  {canManage ? (
                    <button onClick={() => togglePaid(it)}
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full border cursor-pointer ${it.paid ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                      {it.paid ? 'Bezahlt' : 'Offen'}
                    </button>
                  ) : (
                    it.paid
                      ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Bezahlt</span>
                      : <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Offen</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {!user ? (
          <p className="text-xs text-slate-400 border-t border-slate-100 pt-3">
            <Link to="/login" className="text-ocean-600 hover:underline">Melde dich an</Link>, um mitzubestellen.
          </p>
        ) : run.is_locked ? (
          <p className="text-xs text-slate-400 border-t border-slate-100 pt-3">Die Bestellung ist geschlossen.</p>
        ) : (
          <form onSubmit={submitItem} className="border-t border-slate-100 pt-4 flex flex-wrap items-end gap-3">
            {itemError && <div className="alert-error w-full text-xs">{itemError}</div>}
            <FormField label="Deine Bestellung" className="mb-0 flex-1 min-w-[180px]">
              <Input value={itemText} onChange={(e) => setItemText(e.target.value)} placeholder="z.B. Pizza Margherita" required />
            </FormField>
            <FormField label="Preis (€)" className="mb-0">
              <Input type="number" step="0.01" min="0" value={itemPrice} onChange={(e) => setItemPrice(e.target.value)} className="w-24" />
            </FormField>
            <Button type="submit" size="sm" loading={itemBusy}>{myItem ? 'Speichern' : 'Bestellen'}</Button>
            {myItem && <Button type="button" variant="danger" size="sm" onClick={removeItem} loading={itemBusy}>Entfernen</Button>}
          </form>
        )}
      </div>

      {/* Manage */}
      {canManage && (
        <div className="card">
          <p className="section-title mb-3">Verwalten</p>
          <div className="flex flex-wrap gap-2 items-center">
            <FormField label="Status" className="mb-0">
              <Select value={run.status} onChange={(e) => changeStatus(e.target.value)}>
                <option value="open">Offen</option>
                <option value="closed">Geschlossen</option>
                <option value="cancelled">Abgesagt</option>
              </Select>
            </FormField>
            <Button variant="danger" size="sm" onClick={deleteRun} className="ml-auto">Food Run löschen</Button>
          </div>
        </div>
      )}
    </div>
  );
}
